import type { AddonDietAddonsRead } from "@repo/api/catalog/api/schemas/addon-diet-addons-read";
import type { DietAddonGroupDietAddonsRead } from "@repo/api/catalog/api/schemas/diet-addon-group-diet-addons-read";
import { differenceInCalendarDays, format, parseISO } from "date-fns";
import type { ConfigurationItemAddon } from "../types/configuration-item-addon.type";
import type {
  SubItemPricingItem,
  SubItemsPricing,
} from "../types/sub-Items-pricing.type";

const RANGE_DATE_FORMAT = "dd.MM";

// Group a set of yyyy-MM-dd dates into consecutive ranges, each formatted as
// "dd.MM" (single day) or "dd.MM - dd.MM" (span). Used to summarise on which
// days an addon was attached without listing every single date.
export const formatDateRanges = (dates: string[]): string[] => {
  const sorted = [...new Set(dates)]
    .map((value) => parseISO(value))
    .sort((a, b) => a.getTime() - b.getTime());

  const ranges: string[] = [];
  let start: Date | null = null;
  let previous: Date | null = null;

  const pushRange = (from: Date, to: Date) => {
    const fromLabel = format(from, RANGE_DATE_FORMAT);
    const toLabel = format(to, RANGE_DATE_FORMAT);
    ranges.push(
      fromLabel === toLabel ? fromLabel : `${fromLabel} - ${toLabel}`,
    );
  };

  for (const date of sorted) {
    if (start === null || previous === null) {
      start = date;
      previous = date;
      continue;
    }
    if (differenceInCalendarDays(date, previous) === 1) {
      previous = date;
      continue;
    }
    pushRange(start, previous);
    start = date;
    previous = date;
  }
  if (start && previous) {
    pushRange(start, previous);
  }

  return ranges;
};

// A single addon aggregated across all the days it was attached to. The summary
// list shows every addon exactly once, regardless of how many days it covers.
export type SelectedAddon = {
  addon: AddonDietAddonsRead;
  // Total units across every day (sum of per-day quantities).
  totalQuantity: number;
  // Number of distinct days this addon is attached to.
  daysCount: number;
  // Charged price per unit, without the deposit — the quoted `final` carries the
  // deposit, so it is subtracted before dividing. Matches the `unitPrice` the
  // order history gets from the backend.
  unitPrice: number;
  // Unit price before addon discounts (the quoted `before`, already deposit
  // free); equals unitPrice as long as nothing discounts addons (promotion
  // scopes for them arrive with MPL-3614).
  originalPrice: number;
  hasPromo: boolean;
  // One-time refundable deposit (kaucja) per unit.
  depositPerUnit: number;
  // Total for this addon: sum of the quoted entry finals, deposits included.
  productTotal: number;
  // Deposit share of productTotal for this addon, reported separately so it can
  // be shown on its own line.
  depositTotal: number;
  // Consecutive day ranges the addon covers, formatted for chips.
  dateRanges: string[];
};

export type SelectedAddonsSummary = {
  items: SelectedAddon[];
  // Total number of addon units selected across every addon and day.
  totalQuantity: number;
  // Total of every addon — matches subItemsPricing.final of the position, which
  // is the addon share the cart total includes, deposits among it.
  productTotal: number;
  // Sum of every deposit — a share of productTotal, not an amount on top of it.
  depositTotal: number;
  // Distinct delivery days that carry at least one addon.
  daysCount: number;
};

// Catalog list price of an addon, used only for entries the backend has not
// quoted yet (see summarizeSelection).
const estimateEntry = (addon: AddonDietAddonsRead, quantity: number) => {
  const unitPrice = addon.promoPrice ?? addon.price;
  const unitDeposit = addon.deposits.reduce(
    (sum, deposit) => sum + deposit.cost,
    0,
  );

  // Shaped like a quoted entry: `final` carries the deposit, `before` does not.
  return {
    final: quantity * (unitPrice + unitDeposit),
    before: quantity * addon.price,
    deposit: quantity * unitDeposit,
  };
};

const perUnit = (total: number, quantity: number) =>
  quantity > 0 ? total / quantity : 0;

// Turn the flat store entries into a per-addon summary, enriched with catalog
// data (name, image) resolved from the fetched categories. Entries whose addon
// is no longer available in the catalog are skipped defensively.
//
// Every amount comes from `pricing` — the position's subItemsPricing section,
// matched to an entry by (id, date) — because the contract returns entry totals
// only and the catalog price is not what the cart charges. A catalog estimate
// is used in exactly two cases:
//   - no pricing passed: the addon dialog, where the user is still picking days
//     and no quote exists for the selection yet,
//   - a single entry missing from the quote: the window between a committed
//     change and the PUT response that re-quotes the position.
export const summarizeSelection = (
  categories: DietAddonGroupDietAddonsRead[],
  entries: ConfigurationItemAddon[],
  pricing?: SubItemsPricing | null,
): SelectedAddonsSummary => {
  const addonsById = new Map<string, AddonDietAddonsRead>();
  const order = new Map<string, number>();
  let orderIndex = 0;

  for (const category of [...categories].sort(
    (a, b) => a.position - b.position,
  )) {
    for (const addon of [...category.addons].sort(
      (a, b) => a.position - b.position,
    )) {
      addonsById.set(addon.id, addon);
      order.set(addon.id, orderIndex);
      orderIndex += 1;
    }
  }

  const quotedEntries = new Map<string, SubItemPricingItem>();
  for (const quoted of pricing?.items ?? []) {
    quotedEntries.set(`${quoted.id}|${quoted.date}`, quoted);
  }

  const grouped = new Map<string, ConfigurationItemAddon[]>();
  for (const entry of entries) {
    const list = grouped.get(entry.addonId) ?? [];
    list.push(entry);
    grouped.set(entry.addonId, list);
  }

  const items: SelectedAddon[] = [];
  for (const [addonId, addonEntries] of grouped) {
    const addon = addonsById.get(addonId);
    if (!addon) {
      continue;
    }

    let totalQuantity = 0;
    let productTotal = 0;
    let productTotalBefore = 0;
    let depositTotal = 0;

    for (const entry of addonEntries) {
      const quoted = quotedEntries.get(`${entry.addonId}|${entry.date}`);
      const amounts = quoted
        ? {
            final: quoted.final.gross,
            before: quoted.before.gross,
            deposit: quoted.deposit.gross,
          }
        : estimateEntry(addon, entry.quantity);

      totalQuantity += entry.quantity;
      productTotal += amounts.final;
      productTotalBefore += amounts.before;
      depositTotal += amounts.deposit;
    }

    // The quote reports `final` with the deposit inside and `before` without it,
    // so both per-unit prices are compared deposit free.
    const chargedWithoutDeposit = productTotal - depositTotal;

    items.push({
      addon,
      totalQuantity,
      daysCount: new Set(addonEntries.map((entry) => entry.date)).size,
      unitPrice: perUnit(chargedWithoutDeposit, totalQuantity),
      originalPrice: perUnit(productTotalBefore, totalQuantity),
      hasPromo: productTotalBefore > chargedWithoutDeposit,
      depositPerUnit: perUnit(depositTotal, totalQuantity),
      productTotal,
      depositTotal,
      dateRanges: formatDateRanges(addonEntries.map((entry) => entry.date)),
    });
  }

  items.sort(
    (a, b) => (order.get(a.addon.id) ?? 0) - (order.get(b.addon.id) ?? 0),
  );

  return {
    items,
    totalQuantity: items.reduce((sum, item) => sum + item.totalQuantity, 0),
    productTotal: items.reduce((sum, item) => sum + item.productTotal, 0),
    depositTotal: items.reduce((sum, item) => sum + item.depositTotal, 0),
    daysCount: new Set(entries.map((entry) => entry.date)).size,
  };
};
